import i18n, { SIGNUP_NAMESPACE } from '@/i18n';

const ADULT_AGE = 18;

export type DateComponents = {
  year: number;
  month: number;
  day: number;
};

export type BirthDateValidationResult =
  | { valid: true; isAdult: boolean }
  | { valid: false; message: string };

const BIRTH_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseBirthDateComponents(birthDate: string): DateComponents | null {
  const match = BIRTH_DATE_PATTERN.exec(birthDate);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsedDate = new Date(year, month - 1, day);
  if (
    parsedDate.getFullYear() !== year
    || parsedDate.getMonth() !== month - 1
    || parsedDate.getDate() !== day
  ) {
    return null;
  }
  return { year, month, day };
}

export function toDateComponents(date: Date): DateComponents {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

export function compareDateComponents(left: DateComponents, right: DateComponents): number {
  if (left.year !== right.year) {
    return left.year - right.year;
  }
  if (left.month !== right.month) {
    return left.month - right.month;
  }
  return left.day - right.day;
}

export function isFutureBirthDate(birthDate: string, referenceDate = new Date()): boolean {
  const birthComponents = parseBirthDateComponents(birthDate);
  if (!birthComponents) {
    return false;
  }
  const referenceComponents = toDateComponents(referenceDate);
  return compareDateComponents(birthComponents, referenceComponents) > 0;
}

export function isAdult(birthDate: string, referenceDate = new Date()): boolean {
  const birthComponents = parseBirthDateComponents(birthDate);
  if (!birthComponents) {
    return false;
  }
  const referenceComponents = toDateComponents(referenceDate);
  const adultCutoff: DateComponents = {
    year: referenceComponents.year - ADULT_AGE,
    month: referenceComponents.month,
    day: referenceComponents.day,
  };
  return compareDateComponents(birthComponents, adultCutoff) <= 0;
}

export function validateBirthDate(
  birthDate: string,
  referenceDate = new Date(),
): BirthDateValidationResult {
  const birthComponents = parseBirthDateComponents(birthDate);
  if (!birthComponents) {
    return {
      valid: false,
      message: i18n.t('validation.birthDateInvalid', { ns: SIGNUP_NAMESPACE }),
    };
  }
  if (isFutureBirthDate(birthDate, referenceDate)) {
    return {
      valid: false,
      message: i18n.t('validation.birthDateFuture', { ns: SIGNUP_NAMESPACE }),
    };
  }
  return { valid: true, isAdult: isAdult(birthDate, referenceDate) };
}
